import { type FC, useCallback, useMemo } from "react";
import notification from "@app/services/notification";
import { useScannerRegistration, type ScannerHintConfig, type ScannerRegistrationConfig } from "@app/scanner";
import type { OnsideInvoiceContent } from "../../../../../../api/onsideInvoices";
import { resolveStockFinishedGoodsShipmentContentScan } from "../../helpers/resolveStockFinishedGoodsShipmentContentScan";

const CONTENT_SCAN_HINT: ScannerHintConfig = {
    stateKey: "stock-finished-goods-shipment.card.scan-content",
    title: "Сканируйте СО-номер партии",
    description: "Найдём строку НС по СО-номеру партии и откроем форму подтверждения отгрузки.",
    position: { placement: "bottom-left" },
    indicator: { variant: "scanner" },
};

/** Пропсы компонента {@link StockFinishedGoodsShipmentScanPlaceholder}. */
interface StockFinishedGoodsShipmentScanPlaceholderProps {
    /** Строки текущей НС. */
    contents: OnsideInvoiceContent[];
    /** Включён ли сценарий сканирования номера партии. */
    enabled: boolean;
    /**
     * Открывает форму отгрузки найденной строки.
     * @param contentCode код строки НС
     */
    onOpenContent: (contentCode: number) => void;
}

/** Регистрирует сценарий сканирования номера партии в карточке отгрузки СГП. */
export const StockFinishedGoodsShipmentScanPlaceholder: FC<StockFinishedGoodsShipmentScanPlaceholderProps> = ({
    contents,
    enabled,
    onOpenContent,
}) => {
    const handleScan = useCallback(
        async (barcode: string): Promise<void> => {
            const result = await resolveStockFinishedGoodsShipmentContentScan(barcode, contents);

            if (result.type === "notFound") {
                notification.warning(`В НС не найдена строка для СО-номера партии "${barcode}".`);
                return;
            }

            if (result.type === "ambiguous") {
                notification.warning(`Для СО-номера партии "${barcode}" найдено несколько строк. Откройте строку вручную.`);
                return;
            }

            onOpenContent(result.contentCode);
        },
        [contents, onOpenContent],
    );

    const scannerRegistration = useMemo(
        (): ScannerRegistrationConfig => ({
            enabled,
            priority: "screen",
            captureInEditable: false,
            hint: CONTENT_SCAN_HINT,
            onScan: handleScan,
            errorSource: "stock.finishedGoodsShipment.card.scanContent",
        }),
        [enabled, handleScan],
    );

    useScannerRegistration(scannerRegistration);

    return null;
};
